import { useEffect, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/store/hooks";

import { useDebounce } from "@/hooks/useDebounce";
import { captureException } from "@/utils/sentry";

import { adminLoginAsUser } from "@/store/auth";
import {
  usersGetAll,
  usersUpdateUser,
  usersCreateAgencyForUser,
} from "@/store/users";
import { billingUnsubscribeUserByAdmin } from "@/store/billing";
import { usersDeleteUser } from "@/store/users";

import { User } from '@/types'

import AppModal from "@/components/AppModal";
import AppText from "@/components/AppText";
import AppBox from "@/components/AppBox";
import AppButton from "@/components/AppButton";
import AddMailboxModal from "@/components/Modals/AddMailboxModal";
import ReportModal from "@/components/Modals/ReportModal";
import AppInput from "@/components/AppInput";
import AdminUsersTable from "@/components/Tables/AdminUsersTable";
import AppPageContainer from "@/components/AppPageContainer";
import TableSkeleton from "@/components/AppSkeleton/TableSkeleton"
import AppPagination from "@/components/Tables/AppPagination";

const Dashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const { users, status, pagination } = useAppSelector((state) => state.users);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [authCode, setAuthCode] = useState<string>('');
  const [reportUser, setReportUser] = useState<User>();
  const [showReportModal, setShowReportModal] = useState<boolean>(false);
  const [pageTitle, setPageTitle] = useState<string>("");
  const [inputValue, setInputValue] = useState<string>("");
  const [page, setPage] = useState(1);

  const livePage = location.pathname === "/panel";
  const canceledPage = location.pathname === "/panel/canceled";

  // Use debounce for search with 1000ms delay
  const debouncedSearch = useDebounce(inputValue, 1000);

  useEffect(() => {
    dispatch(usersGetAll({ 
      page, 
      perPage: pagination?.per_page || 50,
      search: debouncedSearch || undefined 
    })).catch((error) => {
      captureException(error, {
        component: 'Dashboard',
        action: 'usersGetAll',
        page,
        search: debouncedSearch,
      });
    });
  }, [dispatch, page, debouncedSearch]);

  useEffect(() => {
    // Map routes to titles
    const pageTitles: Record<string, string> = {
      "/panel": "Live Users",
      "/panel/agencies": "Agencies",
      "/panel/canceled": "Canceled Users",
    };

    // Update the document title based on the current URL
    const currentPath = location.pathname;
    const title = pageTitles[currentPath] || "Admin Panel";
    setPageTitle(title)
    setInputValue('')
  }, [location]);

  useEffect(() => {
    const code = searchParams.get("code");
    if (code) {
      setAuthCode(code);
      setIsOpen(true);
    }
  }, [searchParams]);

  const handleToggleReportModal = (user?: User) => {
    setReportUser(user);
    setShowReportModal((prev) => !prev);
  };

  const handleToggleAgency = async (user: User) => {
    try {
      await dispatch(
        usersCreateAgencyForUser({ id: user.id, data: { ...user, is_agency_admin: !user.is_agency_admin } })
      );
    } catch (error) {
      captureException(error as Error, {
        component: 'Dashboard',
        action: 'handleToggleAgency',
        userId: user.id,
      });
    }
  };

  const handleToggleSettings = async (userId: number, field: string, currentValue: boolean) => {
    try {
      // Update the selected user first
      await dispatch(usersUpdateUser({ id: userId, data: { [field]: !currentValue } })).unwrap();


    } catch (error) {
      captureException(error as Error, {
        component: 'Dashboard',
        action: 'handleToggleSettings',
        userId,
        field,
        currentValue,
      });
      console.error("Failed to update user setting:", error);
    }
  };

  const handleDeleteUser = async (user: User) => {
    try {
      await dispatch(billingUnsubscribeUserByAdmin(user.id));
      await dispatch(usersDeleteUser(user.id));
      dispatch(usersGetAll({ page, perPage: pagination?.per_page || 50 }));
    } catch (error) {
      captureException(error as Error, {
        component: 'Dashboard',
        action: 'handleDeleteUser',
        userId: user.id,
      });
    }
  };

  const handleLoginUnderUser = async (user: User) => {
    try {
      await dispatch(adminLoginAsUser(user.id));
      const pageToNavigate = user.is_agency_admin ? "/agency" : "/";
      navigate(pageToNavigate);
    } catch (error) {
      captureException(error as Error, {
        component: 'Dashboard',
        action: 'handleLoginUnderUser',
        userId: user.id,
      });
    }
  };

  const handleChangeGroupText = async (user: User, phone?: string) => {
    try {
      if (phone) {
        const phones = user.phones || [];
        const updatedPhones = phone
        ? [
            ...phones.map((p) => ({
              id: p.id,
              phone: p.phone,
              is_primary: p.is_primary,
            })), // Include existing phones
            { phone, is_primary: true }, // Add new phone with required fields
          ]
        : phones;

        await dispatch(usersUpdateUser({ id: user.id, data: { phones: updatedPhones } })).unwrap();
      }
      handleToggleSettings(user.id, "group_text", user.group_text);
    } catch (error) {
      captureException(error as Error, {
        component: 'Dashboard',
        action: 'handleChangeGroupText',
        userId: user.id,
        phone,
      });
      console.error("Failed to update user:", error);
    }
  };

  const handleCloseMailoxesModal = async () => {
    setAuthCode('')
  }

  return (
    <AppPageContainer
      title={pageTitle}
      titleAdorment={
        <AppBox align="center" gap={4}>
          <AppInput
            placeholder="Search Users"
            value={inputValue}
            onChange={(value) => setInputValue(value)}
            className="w-[320px]"
          />
          {livePage && (
            <AppButton onClick={() => setIsOpen(true)}>
              Manage Mailbox
            </AppButton>
          )}
        </AppBox>
      }
    >
      <AppBox height="95%" column gap={2} className="relative">
        <TableSkeleton.AdminUsers rowCount={5} loading={status === "loading"} />
        <AdminUsersTable
          tableRows={users.filter((user) => livePage ? !user.deleted_at : user.deleted_at) as User[]}
          onLoginUnderUser={handleLoginUnderUser}
          onDelete={handleDeleteUser}
          onChangeCreateAgency={handleToggleAgency}
          onClickReport={handleToggleReportModal}
          onChangeGroupText={handleChangeGroupText}
          onChangeSetting={handleToggleSettings}
          isCanceled={canceledPage}
          className={`transition-opacity ${status === "loading" ? 'opacity-0' : 'opacity-100'}`}
        />
        <AppPagination
          totalRows={pagination?.total || 0}
          rowsPerPage={pagination?.per_page || 50}
          currentPage={(pagination?.current_page || 1) - 1}
          onPageChange={(newPage) => dispatch(usersGetAll({ 
            page: newPage + 1, 
            perPage: pagination?.per_page || 50,
            search: debouncedSearch || undefined
          }))}
          onRowsPerPageChange={(newRowsPerPage) => dispatch(usersGetAll({ 
            page: 1, 
            perPage: newRowsPerPage,
            search: debouncedSearch || undefined
          }))}
        />
      </AppBox>

      <AppModal isOpen={isOpen} title="Add Mailbox" onClose={() => setIsOpen(!isOpen)}>
        <AddMailboxModal authCode={authCode} onMaliboxSaved={handleCloseMailoxesModal} />
      </AppModal>

      <AppModal isOpen={showReportModal} custom title="User Report" onClose={handleToggleReportModal}>
        <ReportModal user={reportUser} onClose={handleToggleReportModal} />
      </AppModal>
    </AppPageContainer>
  );
};

export default Dashboard;